import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { toast } from 'react-toastify';

import { API_ENDPOINT } from '../../constants';
import { fetchWrapper } from '../../helpers';

import type { ActionReducerMapBuilder, PayloadAction } from '@reduxjs/toolkit';
import type { IMaladaptiveBehavior } from '../../utils/types';

const name = 'maladaptiveBehavior';
const initialState = createInitialState();
const reducers = createReducers();
// SAFETY: Legacy thunk action creators use the store dispatch result contract.
const extraActions = createExtraActions() as any;
const slice = createSlice({
  name,
  initialState,
  reducers,
  extraReducers: (builder: ActionReducerMapBuilder<MaladaptiveBehaviorState>) =>
    createExtraReducers(builder),
});

export interface MaladaptiveBehaviorState {
  error: any;
  list: Array<IMaladaptiveBehavior>;
  loading: boolean;
}

function createInitialState(): MaladaptiveBehaviorState {
  return {
    loading: false,
    list: [],
    error: null,
  };
}

function createReducers() {
  return {
    clearState,
  };

  function clearState(state: MaladaptiveBehaviorState) {
    state.error = null;
    state.list = [];
    state.loading = false;
  }
}

function createExtraActions() {
  const baseUrl = `${API_ENDPOINT}/treatment_plans`;

  return {
    getMaladaptiveBehavior: getMaladaptiveBehavior(),
    createMaladaptiveBehavior: createMaladaptiveBehavior(),
    updateMaladaptiveBehavior: updateMaladaptiveBehavior(),
    deleteMaladaptiveBehavior: deleteMaladaptiveBehavior(),
  };

  function getMaladaptiveBehavior() {
    return createAsyncThunk(
      `${name}/getMaladaptiveBehavior`,
      async ({ getAccessTokenSilently, planId }: any) => {
        const response = await fetchWrapper.get({
          url: `${baseUrl}/${planId}/symptoms/`,
          getAccessTokenSilently,
        });
        return response;
      },
    );
  }

  function createMaladaptiveBehavior() {
    return createAsyncThunk(
      `${name}/createMaladaptiveBehavior`,
      async ({ data, getAccessTokenSilently, planId }: any) => {
        const response = await fetchWrapper.post(
          { url: `${baseUrl}/${planId}/symptoms/`, getAccessTokenSilently },
          data,
        );
        return response;
      },
    );
  }

  function updateMaladaptiveBehavior() {
    return createAsyncThunk(
      `${name}/updateMaladaptiveBehavior`,
      async ({ data, getAccessTokenSilently, planId, symptomId }: any) => {
        const response = await fetchWrapper.patch(
          {
            url: `${baseUrl}/${planId}/symptoms/${symptomId}/`,
            getAccessTokenSilently,
          },
          data,
        );
        return response;
      },
    );
  }

  function deleteMaladaptiveBehavior() {
    return createAsyncThunk(
      `${name}/deleteMaladaptiveBehavior`,
      async ({ getAccessTokenSilently, planId, symptomId }: any) => {
        const response = await fetchWrapper.delete({
          url: `${baseUrl}/${planId}/symptoms/${symptomId}/`,
          getAccessTokenSilently,
        });
        return response;
      },
    );
  }
}

function createExtraReducers(builder: ActionReducerMapBuilder<MaladaptiveBehaviorState>) {
  const {
    getMaladaptiveBehavior,
    createMaladaptiveBehavior,
    deleteMaladaptiveBehavior,
    updateMaladaptiveBehavior,
  } = extraActions;

  builder
    // getMaladaptiveBehavior()
    .addCase(getMaladaptiveBehavior.pending, (state: MaladaptiveBehaviorState) => {
      state.error = null;
      state.loading = true;
    })
    .addCase(
      getMaladaptiveBehavior.fulfilled,
      (state: MaladaptiveBehaviorState, action: PayloadAction<Array<IMaladaptiveBehavior>>) => {
        state.list = action.payload;
        state.loading = false;
      },
    )
    .addCase(getMaladaptiveBehavior.rejected, (state: MaladaptiveBehaviorState, action: any) => {
      state.error = action.error;
      state.loading = false;
      toast.error(state.error.message);
    })
    // createMaladaptiveBehavior()
    .addCase(createMaladaptiveBehavior.pending, (state: MaladaptiveBehaviorState) => {
      state.loading = true;
      state.error = null;
    })
    .addCase(
      createMaladaptiveBehavior.fulfilled,
      (state: MaladaptiveBehaviorState, action: PayloadAction<IMaladaptiveBehavior>) => {
        state.loading = false;
        state.list = [...state.list, action.payload];
      },
    )
    .addCase(createMaladaptiveBehavior.rejected, (state: MaladaptiveBehaviorState, action: any) => {
      state.error = action.error;
      state.loading = false;
      toast.error(state.error.message);
    })
    // updateMaladaptiveBehavior()
    .addCase(updateMaladaptiveBehavior.pending, (state: MaladaptiveBehaviorState) => {
      state.loading = true;
      state.error = null;
    })
    .addCase(
      updateMaladaptiveBehavior.fulfilled,
      (
        state: MaladaptiveBehaviorState,
        action: PayloadAction<IMaladaptiveBehavior, string, { arg: { symptomId: number } }>,
      ) => {
        state.loading = false;

        state.list = state.list.map((symptom) => {
          if (symptom.id === action.meta.arg.symptomId) {
            return { ...symptom, ...action.payload };
          }
          return symptom;
        });
      },
    )
    .addCase(updateMaladaptiveBehavior.rejected, (state: MaladaptiveBehaviorState, action: any) => {
      state.error = action.error;
      state.loading = false;
      toast.error(state.error.message);
    })
    // deleteMaladaptiveBehavior()
    .addCase(deleteMaladaptiveBehavior.pending, (state: MaladaptiveBehaviorState) => {
      state.loading = true;
      state.error = null;
    })
    .addCase(
      deleteMaladaptiveBehavior.fulfilled,
      (
        state: MaladaptiveBehaviorState,
        action: PayloadAction<IMaladaptiveBehavior, string, { arg: { symptomId: number } }>,
      ) => {
        state.loading = false;
        state.list = state.list.filter(
          (maladaptiveBehavior) => maladaptiveBehavior.id !== action.meta.arg.symptomId,
        );
      },
    )
    .addCase(deleteMaladaptiveBehavior.rejected, (state: MaladaptiveBehaviorState, action: any) => {
      state.error = action.error;
      state.loading = false;
      toast.error(state.error.message);
    });
}

export const maladaptiveBehaviorActions = { ...slice.actions, ...extraActions };
export const maladaptiveBehaviorReducer = slice.reducer;
